import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Rocket } from "lucide-react";
import { useLang, STR } from "@/i18n";

const TARGET = new Date("2026-10-01T00:00:00-03:00").getTime();

function parts() {
  const d = Math.max(0, TARGET - Date.now());
  return [
    Math.floor(d / 86400000),
    Math.floor(d / 3600000) % 24,
    Math.floor(d / 60000) % 60,
    Math.floor(d / 1000) % 60,
  ];
}

export default function Countdown() {
  const { lang } = useLang();
  const t = STR[lang];
  const [p, setP] = useState(parts);

  useEffect(() => {
    const id = setInterval(() => setP(parts()), 1000);
    return () => clearInterval(id);
  }, []);

  const units = [t.cd_d, t.cd_h, t.cd_m, t.cd_s];

  return (
    <section data-testid="countdown-section" className="relative py-16 sm:py-20 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_50%_60%_at_50%_50%,rgba(46,107,255,0.08),transparent_70%)] pointer-events-none" />
      <div className="relative max-w-4xl mx-auto px-5 sm:px-8 text-center">
        <motion.span
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="mono inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-sky-300 border border-sky-400/30 bg-sky-400/10 rounded-full px-4 py-2"
        >
          <Rocket className="w-3.5 h-3.5" />
          {t.cd_label}
        </motion.span>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="mt-8 flex justify-center gap-3 sm:gap-5"
        >
          {p.map((v, i) => (
            <div
              key={i}
              data-testid={`countdown-unit-${i}`}
              className="w-[72px] sm:w-[104px] rounded-2xl border border-white/10 bg-[#0c1122]/80 backdrop-blur py-4 sm:py-6 shadow-xl"
            >
              <AnimatePresence mode="popLayout">
                <motion.p
                  key={v}
                  initial={{ y: -14, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 14, opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="font-display font-black text-2xl sm:text-4xl text-white tabular-nums"
                >
                  {String(v).padStart(2, "0")}
                </motion.p>
              </AnimatePresence>
              <p className="mono text-[9px] sm:text-[10px] uppercase tracking-[0.22em] text-amber-300 mt-1.5">
                {units[i]}
              </p>
            </div>
          ))}
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mt-6 text-sm text-slate-400"
        >
          {t.cd_sub}
        </motion.p>
      </div>
    </section>
  );
}
